// Penembak event pixel di BROWSER — lapisan tipis di atas helper murni
// `pixel.ts`. Semua yang menyentuh `window` ada di sini; aturan bentuk payload
// dan guard halaman tetap di pixel.ts supaya teruji.
//
// Fire-and-forget: pelacakan TIDAK BOLEH bisa merusak storefront — setiap
// penembakan dibungkus try/catch dan tak pernah melempar ke pemanggil (sikap
// yang sama dengan VisitorTracker/record_page_view).

import {
  GA4_EVENT_NAMES,
  TIKTOK_EVENT_NAMES,
  isTrackablePage,
  toGa4Params,
  toMetaParams,
  toTiktokParams,
  type PixelEventName,
  type PixelEventPayload,
} from "@/lib/pixel";

/** Vendor yang base script-nya benar-benar ter-inject di halaman ini —
 *  di-set `PixelScripts` setelah injeksi. Tanpa registrasi, TIDAK ada
 *  penembakan: keberadaan `window.dataLayer` saja bukan bukti GTM terpasang
 *  (gtag GA4 juga membuatnya). */
type ActivePixels = { meta: boolean; tiktok: boolean; ga4: boolean; gtm: boolean };

let activePixels: ActivePixels = {
  meta: false,
  tiktok: false,
  ga4: false,
  gtm: false,
};

export function registerActivePixels(next: ActivePixels): void {
  activePixels = next;
}

type PixelWindow = Window & {
  fbq?: (...args: unknown[]) => void;
  ttq?: { track?: (...args: unknown[]) => void; page?: () => void };
  gtag?: (...args: unknown[]) => void;
  dataLayer?: unknown[];
};

/**
 * Boleh menembak DI SINI, SEKARANG? Dicek pada SETIAP penembakan (bukan hanya
 * mount) — navigasi SPA tidak me-remount layout, dan base pixel yang sudah
 * hidup ikut terbawa ke /admin/login; event-nya yang harus berhenti.
 * Non-production dimatikan total (keputusan pemilik 2026-08): di dev/localhost
 * `resolve_store_public` jatuh ke toko fallback, event dev akan menembak pixel
 * PRODUKSI milik toko itu dan mengotori data iklan nyata.
 */
function canFireHere(): boolean {
  if (typeof window === "undefined") return false;
  if (process.env.NODE_ENV !== "production") return false;
  return isTrackablePage(window.location.hostname, window.location.pathname);
}

/**
 * Tembakkan satu event funnel belanja ke semua pixel yang terpasang.
 * TANPA PII — no advanced matching (keputusan pemilik 2026-08): email akun
 * reseller adalah dummy berbasis nomor HP, mengirimnya justru membocorkan
 * nomor HP ke vendor iklan.
 */
export function firePixelEvent(
  event: PixelEventName,
  payload: PixelEventPayload
): void {
  if (!canFireHere()) return;
  const w = window as PixelWindow;

  // Fase 2 — Purchase juga dikirim SERVER-SIDE (Conversions API Meta /
  // Events API TikTok) via route best-effort: tetap terlapor saat fbq/ttq
  // diblokir ad-blocker/ITP, dan ter-dedup terhadap event browser lewat
  // event_id = order_code (tanpa order_code = tanpa dedup = jangan kirim).
  // Digerbangi activePixels: toko tanpa pixel terpasang tak perlu membayar
  // round-trip; route-nya sendiri memutuskan token/ID mana yang tersedia.
  if (
    event === "Purchase" &&
    payload.eventId !== undefined &&
    (activePixels.meta || activePixels.tiktok)
  ) {
    try {
      void fetch("/api/pixel/conversions", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ orderCode: payload.eventId }),
        // keepalive: tetap terkirim walau halaman berpindah (router.push
        // ke /orders terjadi segera sesudah Purchase).
        keepalive: true,
      }).catch(() => undefined);
    } catch {
      /* pelacakan tak boleh mengganggu storefront */
    }
  }

  if (activePixels.meta) {
    try {
      w.fbq?.(
        "track",
        event,
        toMetaParams(payload),
        payload.eventId !== undefined ? { eventID: payload.eventId } : undefined
      );
    } catch {
      /* pelacakan tak boleh mengganggu storefront */
    }
  }

  if (activePixels.tiktok) {
    try {
      const params = toTiktokParams(payload);
      if (payload.eventId !== undefined) {
        w.ttq?.track?.(TIKTOK_EVENT_NAMES[event], params, {
          event_id: payload.eventId,
        });
      } else {
        w.ttq?.track?.(TIKTOK_EVENT_NAMES[event], params);
      }
    } catch {
      /* idem */
    }
  }

  if (activePixels.ga4) {
    try {
      w.gtag?.("event", GA4_EVENT_NAMES[event], toGa4Params(payload));
    } catch {
      /* idem */
    }
  }

  if (activePixels.gtm) {
    try {
      // Pola ecommerce GTM: kosongkan objek `ecommerce` dulu supaya push
      // berikutnya tidak mewarisi items event sebelumnya (perilaku merge
      // dataLayer), lalu push event-nya untuk trigger custom event di GTM.
      w.dataLayer?.push({ ecommerce: null });
      w.dataLayer?.push({
        event: GA4_EVENT_NAMES[event],
        ecommerce: toGa4Params(payload),
      });
    } catch {
      /* idem */
    }
  }
}

/**
 * PageView untuk navigasi SPA (App Router tidak reload halaman — base snippet
 * hanya menembak PageView pertama). Dipanggil `PixelScripts` per perubahan
 * pathname.
 */
export function firePixelPageView(): void {
  if (!canFireHere()) return;
  const w = window as PixelWindow;
  const pagePath = window.location.pathname + window.location.search;

  if (activePixels.meta) {
    try {
      w.fbq?.("track", "PageView");
    } catch {
      /* pelacakan tak boleh mengganggu storefront */
    }
  }
  if (activePixels.tiktok) {
    try {
      w.ttq?.page?.();
    } catch {
      /* idem */
    }
  }
  // GA4 SENGAJA tidak ditembak manual di sini: Enhanced Measurement
  // (default NYALA di properti GA4 baru) sudah otomatis mencatat page_view
  // pada perubahan history SPA — tembakan manual menggandakannya. Properti
  // yang mematikan Enhanced Measurement memang tidak mendapat page_view SPA;
  // itu pilihan setelan GA4 toko, bukan tempat kita menebak.
  if (activePixels.gtm) {
    try {
      w.dataLayer?.push({ event: "page_view", page_path: pagePath });
    } catch {
      /* idem */
    }
  }
}
